import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import {
  createCompatRecaptchaVerifier,
  sendVerificationCodeCompat,
  verifyCodeCompat,
} from '../services/firebaseAuthCompat';
import './PhoneAuthSlide.css';
import logger from '../services/logger';

function PhoneAuthSlide({ onNext, onSkip }) {
  const { t } = useTranslation('phoneAuth');

  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [step, setStep] = useState('phone'); // 'phone' ou 'code'
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [recaptchaVerifier, setRecaptchaVerifier] = useState(null);

  // Utiliser useRef pour que le container ne soit jamais recréé par React
  const recaptchaContainerRef = useRef(null);

  useEffect(() => {
    let verifier = null;

    // Nettoyer le conteneur reCAPTCHA au montage
    const container = document.getElementById('recaptcha-container');
    if (container) {
      container.innerHTML = '';
    }

    // Initialiser le RecaptchaVerifier avec Firebase Compat en mode INVISIBLE
    // pour éviter le test visuel qui cause l'erreur "Cannot read properties of null"
    const initRecaptcha = async () => {
      try {
        verifier = await createCompatRecaptchaVerifier('recaptcha-container', true);
        setRecaptchaVerifier(verifier);
        logger.log('✅ RecaptchaVerifier Compat initialisé (mode invisible)');
      } catch (err) {
        logger.error('Erreur lors de l\'initialisation du reCAPTCHA:', err);
        setError(t('errors.recaptchaInit'));
      }
    };

    initRecaptcha();

    // Nettoyer le verifier au démontage
    return () => {
      if (verifier) {
        try {
          verifier.clear();
        } catch (err) {
          logger.error('Erreur lors du nettoyage du reCAPTCHA:', err);
        }
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePhoneChange = (value) => {
    setPhoneNumber(value || '');
    setError('');
  };

  const handleSendCode = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Vérifier que le recaptchaVerifier est initialisé
      if (!recaptchaVerifier) {
        throw new Error(t('errors.recaptchaNotReady'));
      }

      // Validation avec isValidPhoneNumber de react-phone-number-input
      if (!phoneNumber || !isValidPhoneNumber(phoneNumber)) {
        throw new Error(t('errors.invalidPhoneNumber'));
      }

      // phoneNumber est déjà au format E.164 (ex: +33612345678)
      logger.log('📞 Numéro formaté pour Firebase:', phoneNumber);

      // Envoyer le code de vérification avec le verifier existant
      const result = await sendVerificationCodeCompat(
        phoneNumber,
        recaptchaVerifier
      );

      setConfirmationResult(result);

      // Cacher le reCAPTCHA avec une classe CSS (ne touche pas au DOM)
      const recaptchaContainer = document.getElementById('recaptcha-container');
      if (recaptchaContainer) {
        recaptchaContainer.classList.add('hidden');
      }

      // Passer à l'étape du code
      setStep('code');
    } catch (err) {
      logger.error('Erreur lors de l\'envoi du code:', err);

      // Nettoyer et réinitialiser le reCAPTCHA en cas d'erreur
      if (recaptchaVerifier) {
        try {
          recaptchaVerifier.clear();
        } catch (clearError) {
          logger.log('Erreur nettoyage reCAPTCHA:', clearError);
        }
      }

      // Messages d'erreur personnalisés
      let errorMessage = t('errors.sendCodeError');

      if (err.code === 'auth/too-many-requests') {
        errorMessage = t('errors.tooManyRequests');
      } else if (err.code === 'auth/invalid-phone-number') {
        errorMessage = t('errors.invalidPhone');
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (!confirmationResult) {
        throw new Error(t('errors.noConfirmation'));
      }

      if (verificationCode.length !== 6) {
        throw new Error(t('errors.codeLength'));
      }

      // Vérifier le code
      const result = await verifyCodeCompat(confirmationResult, verificationCode);

      // Authentification réussie
      onNext({ user: result.user });
    } catch (err) {
      logger.error('Erreur lors de la vérification:', err);
      setError(
        err.message === 'Firebase: Error (auth/invalid-verification-code).'
          ? t('errors.invalidCode')
          : t('errors.verificationError')
      );
    } finally {
      setLoading(false);
    }
  };

  const handleBackToPhone = async () => {
    setStep('phone');
    setVerificationCode('');
    setError('');
    setConfirmationResult(null);

    // Attendre la fin de l'animation de slide (300ms) avant de toucher au reCAPTCHA
    await new Promise(resolve => setTimeout(resolve, 400));

    // Réafficher le reCAPTCHA en retirant la classe hidden
    // NE PAS toucher au innerHTML pour éviter que reCAPTCHA perde ses références DOM
    const recaptchaContainer = document.getElementById('recaptcha-container');
    if (recaptchaContainer) {
      recaptchaContainer.classList.remove('hidden');
      // Ne pas nettoyer le DOM, juste repositionner le container
    }

    // Le RecaptchaVerifier existant devrait toujours fonctionner
    // On ne le recrée pas pour éviter les conflits
    logger.log('✅ RecaptchaVerifier réaffiché');
  };

  return (
    <div className="slide">
      <div className="slide-content">
        {step === 'phone' ? (
          <>
            <div className="info-box">
              <p className="info-text">
                <strong>{t('infoBox.title')}</strong>
              </p>
              <p className="info-text">
                {t('infoBox.description')}
              </p>
            </div>

            <form onSubmit={handleSendCode} className="auth-form">
              <div className="form-group">
                <label htmlFor="phone" className="form-label">
                  {t('form.phoneLabel')}
                </label>
                <PhoneInput
                  international
                  defaultCountry="FR"
                  value={phoneNumber}
                  onChange={handlePhoneChange}
                  placeholder={t('form.phonePlaceholder')}
                  className="phone-input-international"
                />
              </div>

              {error && <p className="error-message">{error}</p>}

              <div className="navigation-buttons">
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading || !phoneNumber || !isValidPhoneNumber(phoneNumber)}
                >
                  {loading ? t('buttons.sending') : t('buttons.login')}
                </button>
              </div>

              <div className="skip-section">
                <button
                  type="button"
                  className="btn btn-text"
                  onClick={onSkip}
                  disabled={loading}
                >
                  {t('buttons.continueWithoutAccount')}
                </button>
              </div>
            </form>
          </>
        ) : (
          <>
            <p className="info-text">
              {t('verification.codeSentTo')}{' '}
              <strong>{phoneNumber}</strong>
            </p>

            <form onSubmit={handleVerifyCode} className="auth-form">
              <div className="form-group">
                <label htmlFor="code" className="form-label">
                  {t('form.codeLabel')}
                </label>
                <input
                  id="code"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength="6"
                  value={verificationCode}
                  onChange={(e) => {
                    setVerificationCode(e.target.value.replace(/\D/g, ''));
                    setError('');
                  }}
                  placeholder={t('form.codePlaceholder')}
                  className="form-input code-input"
                  required
                  autoComplete="one-time-code"
                />
              </div>

              {error && <p className="error-message">{error}</p>}

              <div className="navigation-buttons">
                <button
                  type="button"
                  onClick={handleBackToPhone}
                  className="btn btn-secondary"
                  disabled={loading}
                >
                  {t('buttons.modifyNumber')}
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading || verificationCode.length !== 6}
                >
                  {loading ? t('buttons.verifying') : t('buttons.verify')}
                </button>
              </div>
            </form>
          </>
        )}

        {/* Conteneur pour reCAPTCHA invisible */}
        <div id="recaptcha-container" ref={recaptchaContainerRef}></div>
      </div>
    </div>
  );
}

export default PhoneAuthSlide;
